import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPlans, createOrder, verifyPayment, getSubscription } from "../utils/api";

export default function SubscribePage() {
  const navigate = useNavigate();
  const { shopId } = useParams();
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [plansRes, subRes] = await Promise.all([getPlans(), getSubscription(shopId)]);
        setPlans(plansRes.data);
        if (subRes.data.subscribed) setCurrentSub(subRes.data.subscription);
      } catch (err) { setError("Could not load plans."); }
      finally { setLoading(false); }
    };
    loadData();
  }, [shopId]);

  const loadRazorpayScript = () => new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const handlePayment = async (plan) => {
    setError(""); setPaying(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) { setError("Could not load payment gateway."); setPaying(false); return; }

      const orderRes = await createOrder({ plan_id: plan.id, shop_id: shopId });
      const { order_id, amount, currency, key_id } = orderRes.data;

      const options = {
        key: key_id, amount, currency,
        name: "TrimSpot",
        description: `${plan.name} Plan`,
        order_id,
        handler: async (response) => {
          try {
            const verifyRes = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              shop_id: shopId, plan_id: plan.id,
            });
            setSuccess(verifyRes.data);
          } catch (err) { setError("Payment verification failed."); }
          finally { setPaying(false); }
        },
        theme: { color: "#e94560" },
        modal: { ondismiss: () => setPaying(false) },
      };
      new window.Razorpay(options).open();
    } catch (err) {
      setError(err.response?.data?.message || "Payment failed.");
      setPaying(false);
    }
  };

  if (loading) return <div style={styles.center}><p style={{ color: "var(--text-muted)" }}>Loading plans...</p></div>;

  // Success Screen
  if (success) {
    return (
      <div style={styles.center}>
        <div style={styles.successCard}>
          <p style={{ fontSize: "60px", marginBottom: "12px" }}>🎉</p>
          <h2 style={styles.successTitle}>Subscription Activated!</h2>
          <p style={styles.successText}>Your <strong style={{ color: "white" }}>{success.plan_name}</strong> plan is now active.</p>
          <div style={styles.successDetails}>
            <div style={styles.successRow}><span style={{ color: "var(--text-muted)" }}>Payment ID</span><span style={{ color: "white", fontSize: "12px" }}>{success.payment_id}</span></div>
            <div style={styles.successRow}>
              <span style={{ color: "var(--text-muted)" }}>Valid Till</span>
              <span style={{ color: "#4caf50", fontWeight: "bold" }}>
                {new Date(success.valid_till).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
              </span>
            </div>
          </div>
          <div style={styles.successNote}>✅ Customers can now book appointments at your shop!</div>
          <button onClick={() => navigate("/dashboard")} style={styles.dashBtn}>Go to Dashboard →</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate("/dashboard")} style={styles.backBtn}>← Back</button>
        <h2 style={styles.headerTitle}>Choose a Plan</h2>
        <div />
      </div>

      <div style={styles.container}>
        <div style={styles.titleSection}>
          <h1 style={styles.title}>📋 Subscription Plans</h1>
          <p style={styles.subtitle}>Subscribe to start receiving bookings from customers</p>
        </div>

        {/* Current Sub Banner */}
        {currentSub && (
          <div style={styles.currentSubBanner}>
            ✅ Active: <strong>{currentSub.plan_name}</strong> · Valid till{" "}
            <strong>{new Date(currentSub.end_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</strong>
          </div>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}

        {/* Plans Grid */}
        <div style={styles.plansGrid}>
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isSelected={selectedPlan?.id === plan.id}
              isCurrent={currentSub?.plan_id === plan.id}
              isPopular={plan.name === "Growth"}
              onSelect={() => setSelectedPlan(plan)}
              onPay={() => handlePayment(plan)}
              paying={paying && selectedPlan?.id === plan.id}
            />
          ))}
        </div>

        {/* FAQ */}
        <div style={styles.faqSection}>
          <h3 style={styles.faqTitle}>❓ Common Questions</h3>
          {[
            { q: "When does subscription start?", a: "Immediately after payment. Customers can book right away!" },
            { q: "Auto renewal?", a: "No! You manually renew each month. No auto-deductions." },
            { q: "What if subscription expires?", a: "Shop stays visible but customers can't book until you renew." },
            { q: "Is test mode real money?", a: "No! Use UPI: success@razorpay for testing. Zero real money." },
          ].map((faq, i) => <FAQ key={i} q={faq.q} a={faq.a} />)}
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, isSelected, isCurrent, isPopular, onSelect, onPay, paying }) {
  const features = plan.features || [];
  const planColors = { "Starter": "#4fc3f7", "Growth": "#e94560", "Premium": "#ff9800" };
  const color = planColors[plan.name] || "#e94560";

  return (
    <div
      onClick={onSelect}
      style={{
        ...styles.planCard,
        border: isSelected || isPopular ? `1px solid ${color}` : "1px solid var(--border)",
        boxShadow: isSelected ? `0 0 20px ${color}22` : "none",
      }}
    >
      {isPopular && <div style={{ ...styles.popularBadge, background: color }}>⭐ Most Popular</div>}
      {isCurrent && <div style={styles.currentBadge}>✅ Current</div>}

      <h3 style={{ ...styles.planName, color }}>{plan.name}</h3>
      <div style={styles.planPrice}>
        <span style={{ ...styles.priceAmount, color }}>₹{plan.price}</span>
        <span style={styles.pricePeriod}>/month</span>
      </div>
      <p style={styles.bookingLimit}>
        {plan.max_bookings ? `Up to ${plan.max_bookings} bookings/month` : "Unlimited bookings"}
      </p>

      <div style={styles.planDivider} />

      <ul style={styles.featuresList}>
        {features.map((feature, i) => (
          <li key={i} style={styles.featureItem}>✓ {feature}</li>
        ))}
      </ul>

      <button
        onClick={(e) => { e.stopPropagation(); onPay(); }}
        style={{ ...styles.payBtn, background: color }}
        disabled={paying}
      >
        {paying ? "Processing..." : isCurrent ? "Renew Plan" : "Subscribe Now"}
      </button>
    </div>
  );
}

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.faqItem} onClick={() => setOpen(!open)}>
      <div style={styles.faqQ}>
        <span style={{ color: "var(--text-primary)" }}>{q}</span>
        <span style={{ color: "var(--text-muted)" }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && <p style={styles.faqA}>{a}</p>}
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)", paddingBottom: "60px" },
  center: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh", padding: "20px" },

  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", background: "var(--bg-secondary)", borderBottom: "1px solid var(--border)" },
  headerTitle: { fontSize: "17px", fontWeight: "bold", color: "white" },
  backBtn: { background: "none", border: "1px solid var(--border)", color: "var(--text-secondary)", padding: "8px 14px", borderRadius: "6px", cursor: "pointer", fontSize: "14px" },

  container: { maxWidth: "900px", margin: "0 auto", padding: "24px 16px" },
  titleSection: { textAlign: "center", marginBottom: "28px" },
  title: { fontSize: "clamp(1.4rem, 4vw, 2rem)", fontWeight: "900", color: "white", marginBottom: "8px" },
  subtitle: { color: "var(--text-muted)", fontSize: "14px" },

  currentSubBanner: { background: "rgba(76,175,80,0.1)", color: "#4caf50", padding: "12px 16px", borderRadius: "10px", marginBottom: "20px", fontSize: "14px", border: "1px solid rgba(76,175,80,0.3)", lineHeight: 1.6 },
  errorBox: { background: "rgba(233,69,96,0.1)", color: "#ff6b6b", padding: "12px", borderRadius: "8px", marginBottom: "20px", border: "1px solid rgba(233,69,96,0.3)" },

  plansGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px", marginBottom: "40px" },

  planCard: { background: "var(--bg-card)", borderRadius: "16px", padding: "24px 20px", cursor: "pointer", position: "relative", transition: "all 0.2s" },
  popularBadge: { position: "absolute", top: "-14px", left: "50%", transform: "translateX(-50%)", color: "white", padding: "4px 16px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold", whiteSpace: "nowrap" },
  currentBadge: { position: "absolute", top: "12px", right: "12px", background: "rgba(76,175,80,0.15)", color: "#4caf50", padding: "3px 10px", borderRadius: "12px", fontSize: "11px", border: "1px solid rgba(76,175,80,0.3)" },
  planName: { fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px" },
  planPrice: { display: "flex", alignItems: "baseline", gap: "4px", marginBottom: "6px" },
  priceAmount: { fontSize: "2.2rem", fontWeight: "900" },
  pricePeriod: { color: "var(--text-muted)", fontSize: "13px" },
  bookingLimit: { color: "var(--text-muted)", fontSize: "12px", marginBottom: "16px" },
  planDivider: { height: "1px", background: "var(--border)", margin: "16px 0" },
  featuresList: { listStyle: "none", padding: 0, margin: "0 0 20px" },
  featureItem: { color: "var(--text-secondary)", fontSize: "13px", marginBottom: "8px" },
  payBtn: { width: "100%", padding: "12px", color: "white", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "14px", fontWeight: "bold" },

  // Success
  successCard: { background: "var(--bg-card)", borderRadius: "20px", padding: "40px 32px", maxWidth: "440px", width: "100%", textAlign: "center", border: "1px solid var(--border)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" },
  successTitle: { fontSize: "1.6rem", color: "white", marginBottom: "8px" },
  successText: { color: "var(--text-muted)", marginBottom: "20px" },
  successDetails: { background: "var(--bg-secondary)", borderRadius: "10px", padding: "16px", marginBottom: "16px", textAlign: "left", border: "1px solid var(--border)" },
  successRow: { display: "flex", justifyContent: "space-between", marginBottom: "10px", fontSize: "13px" },
  successNote: { background: "rgba(76,175,80,0.1)", color: "#4caf50", padding: "12px", borderRadius: "8px", fontSize: "13px", marginBottom: "20px", border: "1px solid rgba(76,175,80,0.3)" },
  dashBtn: { background: "var(--accent)", color: "white", padding: "13px 28px", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px", fontWeight: "bold" },

  // FAQ
  faqSection: { maxWidth: "600px", margin: "0 auto" },
  faqTitle: { fontSize: "1.1rem", color: "white", marginBottom: "16px", textAlign: "center" },
  faqItem: { background: "var(--bg-card)", borderRadius: "10px", padding: "16px", marginBottom: "8px", cursor: "pointer", border: "1px solid var(--border)" },
  faqQ: { display: "flex", justifyContent: "space-between", fontWeight: "600", fontSize: "13px" },
  faqA: { color: "var(--text-muted)", fontSize: "13px", marginTop: "10px", lineHeight: 1.6 },
};